import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './ConversationHistory.css';

function ConversationHistory() {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    fetchConversations();
  }, []);

  const fetchConversations = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch('http://localhost:5000/api/sessions', {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      const data = await response.json();

      if (response.ok) {
        setConversations(data.sessions || []);
      } else {
        console.error('Failed to fetch conversations:', data.error);
        // Use demo data
        setConversations(getDemoData());
      }
    } catch (error) {
      console.error('Error fetching conversations:', error);
      // Use demo data
      setConversations(getDemoData());
    } finally {
      setLoading(false);
    }
  };

  const getDemoData = () => {
    return [
      {
        id: 1,
        session_id: 'SESS-001',
        patient_name: 'John Doe',
        patient_mrn: 'P12345',
        created_at: '2025-12-28T10:30:00',
        duration_seconds: 1200,
        status: 'completed',
        visit_type: 'Follow-up'
      },
      {
        id: 2,
        session_id: 'SESS-002',
        patient_name: 'Jane Smith',
        patient_mrn: 'P12346',
        created_at: '2025-12-28T14:15:00',
        duration_seconds: 900,
        status: 'completed',
        visit_type: 'Initial Consultation'
      },
      {
        id: 3,
        session_id: 'SESS-003',
        patient_name: 'Robert Johnson',
        patient_mrn: 'P12347',
        created_at: '2025-12-27T09:00:00',
        duration_seconds: 1500,
        status: 'completed',
        visit_type: 'Annual Physical'
      }
    ];
  };

  const handleDeleteClick = (conversation) => {
    setDeleteTarget(conversation);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;

    const token = localStorage.getItem('token');
    
    try {
      const response = await fetch(`http://localhost:5000/api/sessions/${deleteTarget.session_id}/delete`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        credentials: 'include'
      });

      if (response.ok) {
        setConversations(conversations.filter(c => c.id !== deleteTarget.id));
        setShowDeleteConfirm(false);
        setDeleteTarget(null);
        
        if (selectedConversation && selectedConversation.id === deleteTarget.id) {
          setSelectedConversation(null);
        }
      } else {
        alert('Failed to delete conversation');
      }
    } catch (error) {
      console.error('Error deleting conversation:', error);
      alert('Error deleting conversation');
    }
  };

  const handleDeleteAll = () => {
    if (conversations.length === 0) {
      alert('No conversations to delete');
      return;
    }
    
    if (window.confirm(`Are you sure you want to delete all ${conversations.length} conversations? This action cannot be undone.`)) {
      setConversations([]);
      setSelectedConversation(null);
      alert('All conversations have been deleted');
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const filteredConversations = conversations.filter(conv => {
    const matchesSearch = conv.patient_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         conv.patient_mrn?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         conv.session_id?.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filter === 'all') return matchesSearch;
    return matchesSearch && conv.status === filter;
  });

  return (
    <div className="conversation-history-container">
      <div className="history-header">
        <div className="header-top">
          <h1>📋 Conversation History</h1>
          <div className="header-buttons">
            <button className="delete-all-button" onClick={handleDeleteAll}>
              <span className="button-icon">🗑️</span>
              Delete All
            </button>
            <button className="new-recording-button" onClick={() => navigate('/record-session')}>
              <span className="button-icon">➕</span>
              New Recording
            </button>
          </div>
        </div>
        
        <div className="header-controls">
          <div className="search-box">
            <span className="search-icon">🔍</span>
            <input
              type="text"
              placeholder="Search by patient name, MRN, or session ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="filter-buttons">
            <button
              className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
              onClick={() => setFilter('all')}
            >
              All
            </button>
            <button
              className={`filter-btn ${filter === 'completed' ? 'active' : ''}`}
              onClick={() => setFilter('completed')}
            >
              Completed
            </button>
            <button
              className={`filter-btn ${filter === 'pending' ? 'active' : ''}`}
              onClick={() => setFilter('pending')}
            >
              Pending
            </button>
          </div>
        </div>
      </div>

      <div className="history-content">
        {loading ? (
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading conversations...</p>
          </div>
        ) : filteredConversations.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <h2>No conversations found</h2>
            <p>Start by recording a new clinical conversation</p>
            <button className="primary-button" onClick={() => navigate('/record-session')}>
              Start Recording
            </button>
          </div>
        ) : (
          <div className="conversations-grid">
            {filteredConversations.map((conversation) => (
              <div
                key={conversation.id}
                className="conversation-card"
                onClick={() => setSelectedConversation(conversation)}
              >
                <div className="card-header">
                  <div className="patient-info">
                    <h3>{conversation.patient_name}</h3>
                    <span className="patient-mrn">MRN: {conversation.patient_mrn}</span>
                  </div>
                  <div className={`status-badge status-${conversation.status}`}>
                    {conversation.status}
                  </div>
                </div>
                
                <div className="card-body">
                  <div className="info-row">
                    <span className="info-label">Session ID:</span>
                    <span className="info-value">{conversation.session_id}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Date:</span>
                    <span className="info-value">{formatDate(conversation.created_at)}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Duration:</span>
                    <span className="info-value">{formatDuration(conversation.duration_seconds || 0)}</span>
                  </div>
                  <div className="info-row">
                    <span className="info-label">Type:</span>
                    <span className="info-value">{conversation.visit_type || 'General'}</span>
                  </div>
                </div>
                
                <div className="card-actions">
                  <button
                    className="view-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/session/${conversation.session_id}`);
                    }}
                  >
                    👁️ View
                  </button>
                  <button
                    className="delete-button"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteClick(conversation);
                    }}
                  >
                    🗑️ Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>⚠️ Confirm Deletion</h2>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to delete this conversation?</p>
              <div className="delete-info">
                <p><strong>Patient:</strong> {deleteTarget?.patient_name}</p>
                <p><strong>Session ID:</strong> {deleteTarget?.session_id}</p>
                <p><strong>Date:</strong> {formatDate(deleteTarget?.created_at)}</p>
              </div>
              <p className="warning-text">
                This action cannot be undone. The conversation will be marked as deleted 
                but retained for audit purposes.
              </p>
            </div>
            <div className="modal-actions">
              <button
                className="cancel-button"
                onClick={() => {
                  setShowDeleteConfirm(false);
                  setDeleteTarget(null);
                }}
              >
                Cancel
              </button>
              <button
                className="confirm-delete-button"
                onClick={confirmDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Conversation Detail Modal */}
      {selectedConversation && !showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setSelectedConversation(null)}>
          <div className="modal-content detail-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Conversation Details</h2>
              <button className="close-button" onClick={() => setSelectedConversation(null)}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h3>Patient Information</h3>
                <p><strong>Name:</strong> {selectedConversation.patient_name}</p>
                <p><strong>MRN:</strong> {selectedConversation.patient_mrn}</p>
                <p><strong>DOB:</strong> {selectedConversation.patient_dob}</p>
              </div>
              <div className="detail-section">
                <h3>Session Information</h3>
                <p><strong>Session ID:</strong> {selectedConversation.session_id}</p>
                <p><strong>Date:</strong> {formatDate(selectedConversation.created_at)}</p>
                <p><strong>Duration:</strong> {formatDuration(selectedConversation.duration_seconds || 0)}</p>
                <p><strong>Type:</strong> {selectedConversation.visit_type || 'General'}</p>
                <p><strong>Status:</strong> {selectedConversation.status}</p>
              </div>
            </div>
            <div className="modal-actions">
              <button
                className="primary-button"
                onClick={() => navigate(`/session/${selectedConversation.session_id}`)}
              >
                View Full Details
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ConversationHistory;
